/**
 * ConversationalSurveyFlow — experimental voice-driven, discussion-guide survey.
 *
 * Rendered by SurveySession when survey.ai_features.survey_style === 'conversational'. The
 * traditional one-question-per-screen flow is left completely untouched.
 *
 * Loop: ask the AI moderator for the next question → speak it (browser TTS) → respondent answers by
 * voice (reusing the existing /api/transcribe server transcription) → submit the turn → repeat,
 * until the moderator says it's complete. The voice/transcription logic is intentionally COPIED
 * from SurveySession.tsx rather than refactored, to avoid touching the large working file.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Square, RotateCcw, Volume2, VolumeX, Loader2, Send } from 'lucide-react';
import {
  Survey,
  getNextModeratorQuestion,
  submitModeratorAnswer,
  completeSurveySession,
  ModeratorNextResult,
} from '../../utils/surveyApi';
import { useActiveQuestionTime } from './useActiveQuestionTime';
import { isTimerExpiredWrite } from './surveyTimer';
import { VOICE_CAPTURE } from '../../config/constants';
import { UI_TEXT } from '../../config/ui-text';
import { isBlobLongEnough, isReadyChunk } from '../../utils/audioCapture';

// How a conversational answer was produced, reported as the turn's input_mode.
const INPUT_MODE = { MIC: 'mic', TEXT: 'text' } as const;
type InputMode = (typeof INPUT_MODE)[keyof typeof INPUT_MODE];

interface ConversationalSurveyFlowProps {
  survey: Survey;
  sessionId: number;
  /**
   * The survey-wide countdown and its warnings, rendered above the conversation.
   *
   * Owned by SurveySession, which owns the clock — but this flow replaces the whole respondent
   * body, so without somewhere here to put them a timed conversational survey showed nothing at
   * all: no readout, no warning at any threshold, and then a hard stop that navigated the
   * respondent away mid-conversation with no notice whatsoever.
   */
  timer?: React.ReactNode;
  /**
   * The runtime-wide "a write is in the air" flag, shared with the owner of the survey clock.
   *
   * The hard stop waits on this before asking the server to end the session, because
   * /timer/expire COMPLETES it and every other write is refused afterwards. This flow's own
   * `isSubmitting` is local state the hook cannot see, so a moderator turn in flight was simply
   * not awaited: the session was completed underneath it and the answer discarded.
   */
  submitInFlightRef?: React.MutableRefObject<boolean>;
  /**
   * Called when the server reports that this turn arrived after the survey-wide hard stop.
   *
   * The turn was accepted and NOT recorded, so the conversation must not carry on — and the clock
   * needs telling, since only the server knows the buzzer went.
   */
  onTimerExpired?: () => void;
  /**
   * The owner's copy of "the server's buzzer has already gone".
   *
   * Reported UP by `onTimerExpired` and handed back DOWN, because the runtime that owns the clock
   * is the one that decides. Either way this flow has to STOP: past the hard stop every turn is
   * accepted and discarded, so a Send press buys a wasted POST on the unauthenticated respondent
   * path, clears the notice that says why, and re-takes the runtime-wide `submitInFlightRef` that
   * the hard stop is sitting waiting on — pushing the stop out towards its budget once per press.
   */
  timerExpired?: boolean;
  /**
   * Called whenever the microphone starts or stops capturing.
   *
   * The survey-wide clock treats a live mic as presence, because recording fires none of the
   * input events it otherwise watches for. That flag was wired to the traditional question
   * body's own recording state, which is never set here — so the one runtime that is voice-first
   * by design reported every long answer as an absence of exactly its own length.
   */
  onRecordingChange?: (recording: boolean) => void;
}

const ConversationalSurveyFlow: React.FC<ConversationalSurveyFlowProps> = ({
  survey,
  sessionId,
  timer,
  submitInFlightRef,
  onTimerExpired,
  timerExpired = false,
  onRecordingChange,
}) => {
  const navigate = useNavigate();

  const [current, setCurrent] = useState<ModeratorNextResult | null>(null);
  const [response, setResponse] = useState('');
  const [questionStartTime, setQuestionStartTime] = useState<string>('');
  const [isThinking, setIsThinking] = useState(true); // moderator is deciding the next question
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /**
   * The synchronous half of that, and the one the re-entrancy guard is written against.
   *
   * `isSubmitting` is state, so two Sends in the same tick both read the old `false` and both
   * POST — SurveySession's own `handleSubmitResponse` carries a ref for exactly this reason and
   * says so. Here it was worse than a duplicate write: this handler takes the runtime-wide
   * `submitInFlightRef` the hard stop polls, and the first turn's `finally` gave that flag back
   * while the second was still in flight — so `submitToSettle()` resolved and /timer/expire
   * completed the session underneath a live write, which is the precise failure the shared ref
   * was introduced to prevent.
   */
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [turnCount, setTurnCount] = useState(0);
  const [coveredTopics, setCoveredTopics] = useState<string[]>([]);
  // Track how the current answer was produced so we report the real input mode (not always 'mic').
  // Whichever happened most recently before submit wins: transcription -> 'mic', typing -> 'text'.
  // The textarea is locked while transcribing, so a transcription can't clobber a deliberate edit.
  const [lastInputMode, setLastInputMode] = useState<InputMode>(INPUT_MODE.TEXT);
  /**
   * The buzzer as this flow knows it: what the owner of the clock says, OR what a discarded turn
   * of our own has already proved — derived, so there is no second copy to fall out of step.
   *
   * The ref beside it carries BOTH halves, and exists because the local one is set SYNCHRONOUSLY:
   * a press landing before React commits would get straight past a guard written against state
   * alone. The same reason SurveySession keeps `submitInFlightRef` beside `isSubmitting`.
   *
   * It carries the owner's half too — latched by the release effect below — because it is what an
   * async path suspended across an `await` has to read. A callback that woke up and checked the
   * `timerExpired` PROP would be checking the value from the render it was created in.
   */
  const [expiredHere, setExpiredHere] = useState(false);
  const expiredRef = useRef(false);
  const expired = timerExpired || expiredHere;
  const markExpired = useCallback(() => {
    expiredRef.current = true;
    setExpiredHere(true);
  }, []);

  // True active-time per moderator turn. This flow only renders for real persisted sessions
  // (SurveySession's preview mode never reaches it), so tracking is always enabled here.
  const { getTimings: getActiveTimings, resetForQuestion: resetActiveTime } =
    useActiveQuestionTime(true);

  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [voiceOn, setVoiceOn] = useState(ttsSupported);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [micStatus, setMicStatus] = useState<'idle' | 'warming' | 'listening'>('idle');
  const readinessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readinessFailedRef = useRef(false);
  /**
   * Whether there is a take running that the respondent can still do something with.
   *
   * Derived from the buzzer rather than tracked beside it, because past `expired` there is no such
   * take by definition. That mic button is a STOP button for as long as one is running, so
   * `expired` disabling it removes the stop rather than the start — and everything keyed on
   * `micStatus` alone went on describing a recording the respondent had no way left to end: a red
   * pulsing square over "Listening… tap to stop", on a control that cannot be tapped, and the
   * clock above still being told they were engaged.
   *
   * `expired` only ever goes one way (`expiredHere` latches, and the owner's `timerExpired` is set
   * true and never cleared), so `micStatus` left standing behind this can never resurface.
   */
  const micLive = !expired && micStatus !== 'idle';

  // The survey-wide clock's blind spot, reported UP because that clock is SurveySession's and
  // this is the only place that knows the mic is live. Recording fires no input event of any
  // kind, so a respondent talking for three minutes reads to the server as three minutes of
  // absence: it pauses their clock, welcomes them back from somewhere they never went, and on a
  // hard stop hands them the length of everything they said back as extra time.
  //
  // Through a ref so an inline callback costs no extra effect run, and reported false on the way
  // out — left true by a flow torn down mid-recording, the clock could never pause again.
  const onRecordingChangeRef = useRef(onRecordingChange);
  useEffect(() => {
    onRecordingChangeRef.current = onRecordingChange;
  }, [onRecordingChange]);
  useEffect(() => {
    onRecordingChangeRef.current?.(micLive);
  }, [micLive]);
  useEffect(
    () => () => {
      onRecordingChangeRef.current?.(false);
    },
    [],
  );

  // ---------------------------------------------------------------- text-to-speech (browser)
  const speak = useCallback(
    (text?: string | null) => {
      if (!voiceOn || !ttsSupported || !text) return;
      try {
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(text);
        utter.rate = 1.0;
        utter.pitch = 1.0;
        window.speechSynthesis.speak(utter);
      } catch {
        /* TTS is best-effort; ignore failures */
      }
    },
    [voiceOn, ttsSupported],
  );

  const stopSpeaking = useCallback(() => {
    if (ttsSupported) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
  }, [ttsSupported]);

  // ----------------------------------------------------------------- moderator turn loop
  const fetchNextQuestion = useCallback(async () => {
    setIsThinking(true);
    setError(null);
    try {
      const result = await getNextModeratorQuestion(sessionId);
      if (result.action === 'complete') {
        // Optional spoken closing line before we finish.
        if (result.question_text) speak(result.question_text);
        await completeSurveySession(sessionId);
        navigate(`/survey-complete/${sessionId}`);
        return;
      }
      setCurrent(result);
      setCoveredTopics(result.covered_topics || []);
      setResponse('');
      setLastInputMode(INPUT_MODE.TEXT);
      setQuestionStartTime(new Date().toISOString());
      resetActiveTime();
      speak(result.question_text);
    } catch (e) {
      console.error('moderator/next failed:', e);
      setError('Something went wrong reaching the moderator. Please try again.');
    } finally {
      setIsThinking(false);
    }
  }, [sessionId, navigate, speak, resetActiveTime]);

  // Kick off the first question on mount.
  useEffect(() => {
    fetchNextQuestion();
    return () => {
      stopSpeaking();
      // Don't let a pending readiness timeout fire after unmount (navigating away
      // mid-warmup) and setState on a gone component.
      if (readinessTimerRef.current) clearTimeout(readinessTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(async () => {
    // Past the server's buzzer this turn is accepted and recorded nowhere, so the only thing a
    // press can still do is harm: it clears the notice below, POSTs again, and re-takes the flag
    // the hard stop is waiting on. The two respondent-driven paths in SurveySession return here
    // for the same reason; this is the third.
    if (timerExpired || expiredRef.current) return;
    if (!current || !current.turn_id || !response.trim() || submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    // Published where the survey clock can see it as well as held locally, so the hard stop waits
    // for this turn instead of completing the session out from under it.
    if (submitInFlightRef) submitInFlightRef.current = true;
    setError(null);
    stopSpeaking();
    try {
      const activeTimings = getActiveTimings();
      const result = await submitModeratorAnswer(sessionId, {
        turn_id: current.turn_id,
        moderator_question: current.question_text || '',
        response_text: response.trim(),
        is_mandatory: current.is_mandatory,
        mandatory_question_id: current.mandatory_question_id,
        covered_topics: current.covered_topics,
        input_mode: lastInputMode,
        question_start_time: questionStartTime || undefined,
        // Fail-open: when timing capture is unavailable the fields are simply omitted.
        ...(activeTimings ?? {}),
      });
      // Released as soon as the WRITE lands. What follows belongs to the next turn and has
      // nothing left to save, so holding the flag through it would only delay the stop.
      if (submitInFlightRef) submitInFlightRef.current = false;

      // `status: success` with nothing written: the hard stop had already passed, so this turn
      // was discarded server-side. Asking the moderator for another question would carry the
      // respondent further into a conversation none of which is being recorded.
      if (isTimerExpiredWrite(result)) {
        markExpired();
        setError(UI_TEXT.HUMAN_SURVEY.SESSION.ERRORS.TIME_UP);
        onTimerExpired?.();
        return;
      }

      setTurnCount((n) => n + 1);
      await fetchNextQuestion();
    } catch (e) {
      console.error('moderator/answer failed:', e);
      setError('Could not save your answer. Please try again.');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
      if (submitInFlightRef) submitInFlightRef.current = false;
    }
  }, [
    current,
    response,
    sessionId,
    questionStartTime,
    lastInputMode,
    stopSpeaking,
    fetchNextQuestion,
    getActiveTimings,
    submitInFlightRef,
    onTimerExpired,
    markExpired,
    timerExpired,
  ]);

  // ----------------------------------------------------------------- voice capture (copied)
  const pollTranscriptionResult = useCallback(async (taskId: string): Promise<string> => {
    const maxAttempts = 30;
    const pollInterval = 1000;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe/status/${taskId}`);
      if (!res.ok) throw new Error('Failed to check transcription status');
      const result = await res.json();
      if (result.status === 'completed') return result.text || '';
      if (result.status === 'failed') throw new Error(result.error || 'Transcription failed');
      await new Promise((r) => setTimeout(r, pollInterval));
    }
    throw new Error('Transcription timed out');
  }, []);

  const transcribeAudio = useCallback(
    async (audioBlob: Blob, mimeType: string) => {
      try {
        setIsTranscribing(true);
        const ext = mimeType.includes('webm')
          ? '.webm'
          : mimeType.includes('mp4')
            ? '.mp4'
            : mimeType.includes('wav')
              ? '.wav'
              : '.webm';
        const formData = new FormData();
        formData.append('audio', audioBlob, `recording${ext}`);
        formData.append('context_type', 'survey');
        const turnId = current?.turn_id;
        formData.append(
          'context_metadata',
          JSON.stringify({
            session_id: String(sessionId),
            survey_id: String(survey.id),
            ...(turnId ? { turn_id: String(turnId) } : {}),
          }),
        );
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe`, {
          method: 'POST',
          body: formData,
        });
        if (!res.ok) throw new Error(`Transcription failed: ${res.status}`);
        const result = await res.json();
        if (result.status === 'processing' && result.task_id) {
          const text = await pollTranscriptionResult(result.task_id);
          setResponse((prev) => (prev ? `${prev} ${text}` : text));
          setLastInputMode(INPUT_MODE.MIC);
        } else {
          throw new Error(result.error || 'Transcription failed');
        }
      } catch (e) {
        console.error('Transcription error:', e);
        setError(UI_TEXT.HUMAN_SURVEY.CONVERSATIONAL.ERRORS.TRANSCRIBE_FAILED);
      } finally {
        setIsTranscribing(false);
      }
    },
    [pollTranscriptionResult, sessionId, survey, current],
  );

  const startRecording = useCallback(async () => {
    readinessFailedRef.current = false;
    try {
      stopSpeaking(); // don't record the moderator's own voice
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
      });
      // The buzzer can go while that await is in the air, and NOTHING downstream can stop what
      // this function does next. The release effect fired once on the transition and found
      // `mediaRecorderRef` still holding the previous take, so it was a no-op; it will not run
      // again, the mic button is disabled, and `micLive` reads false — so the survey clock above
      // is told the mic stopped while `start()` below actually starts it. On the `navigate()`
      // branch of a hard stop there is no page unload either, so the recording indicator simply
      // stays lit. Let the stream go and give up the take: there is nowhere left to send it.
      if (expiredRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : MediaRecorder.isTypeSupported('audio/wav')
            ? 'audio/wav'
            : '';
      if (!mimeType) throw new Error('No supported audio format found');

      const mediaRecorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (isReadyChunk(event.data.size)) {
          audioChunksRef.current.push(event.data);
          // First real chunk = capture is actually live; only now is it safe to
          // show "recording" (on a slow device the mic isn't capturing yet at start).
          if (readinessTimerRef.current) {
            clearTimeout(readinessTimerRef.current);
            readinessTimerRef.current = null;
            setMicStatus('listening');
          }
        }
      };
      mediaRecorder.onstop = async () => {
        // Readiness timeout already handled failure + cleared chunks; don't run the
        // too-short path over the empty buffer and clobber MIC_NOT_READY.
        if (readinessFailedRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        if (!isBlobLongEnough(audioBlob.size, VOICE_CAPTURE.MIN_BLOB_BYTES)) {
          setError(UI_TEXT.HUMAN_SURVEY.SESSION.ERRORS.RECORDING_TOO_SHORT);
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        await transcribeAudio(audioBlob, mimeType);
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorder.onerror = () => {
        if (readinessTimerRef.current) {
          clearTimeout(readinessTimerRef.current);
          readinessTimerRef.current = null;
        }
        setError(UI_TEXT.HUMAN_SURVEY.SESSION.ERRORS.RECORDING_FAILED);
        setMicStatus('idle');
        stream.getTracks().forEach((t) => t.stop());
      };
      setMicStatus('warming');
      mediaRecorder.start(VOICE_CAPTURE.TIMESLICE_MS);
      // No audio within the window => capture never came up; fail to typing.
      readinessTimerRef.current = setTimeout(() => {
        readinessTimerRef.current = null;
        readinessFailedRef.current = true; // tell onstop to skip the too-short path
        try {
          if (mediaRecorder.state === 'recording') mediaRecorder.stop();
        } catch {
          /* noop */
        }
        stream.getTracks().forEach((t) => t.stop());
        audioChunksRef.current = [];
        setMicStatus('idle');
        setError(UI_TEXT.HUMAN_SURVEY.SESSION.ERRORS.MIC_NOT_READY);
      }, VOICE_CAPTURE.READINESS_TIMEOUT_MS);
    } catch (e) {
      console.error('Error starting recording:', e);
      setError(UI_TEXT.HUMAN_SURVEY.SESSION.ERRORS.MIC_PERMISSION);
      setMicStatus('idle');
    }
  }, [transcribeAudio, stopSpeaking]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (readinessTimerRef.current) {
      clearTimeout(readinessTimerRef.current);
      readinessTimerRef.current = null;
    }
    setMicStatus('idle');
  }, []);

  /**
   * Let go of the microphone at the buzzer, for a take there is no longer anywhere to send.
   *
   * Not `stopRecording`: that ASKS for the clip, and `onstop`'s ordinary path then uploads it to
   * /api/transcribe — an answer the server has already stopped recording — releasing the stream
   * only once that round trip lands, so the browser's recording indicator stays lit for the length
   * of it and for ever if it hangs. Its other branch reports the take too short, over the top of
   * the one notice that explains what actually happened.
   *
   * `readinessFailed` is the existing "this take is being thrown away, just release the stream"
   * flag — the readiness timeout's own exit — and it says exactly that here too. It is cleared by
   * the next `startRecording`, and `expired` never lets there be one.
   *
   * Nothing here touches state: `micLive` already reads false the moment `expired` does, so the
   * screen and the clock above are told by the derivation rather than by a second update.
   */
  const releaseMicrophone = useCallback(() => {
    readinessFailedRef.current = true;
    if (readinessTimerRef.current) {
      clearTimeout(readinessTimerRef.current);
      readinessTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // Both doors onto the buzzer land here: the owner of the clock flipping `timerExpired` under a
  // conversation already in progress, and this flow's own Send coming back `survey_timer_expired`
  // — which is reachable while the mic is still live, since `busy` gates Send on
  // `isThinking || isSubmitting` and recording is in neither.
  useEffect(() => {
    if (!expired) return;
    // Latched here as well as in `markExpired`, which only ever sees this flow's OWN discarded
    // turn. `startRecording` suspended on `await getUserMedia` wakes up long after this effect has
    // run and been forgotten, and the ref is the only thing it can ask.
    expiredRef.current = true;
    releaseMicrophone();
  }, [expired, releaseMicrophone]);

  // ----------------------------------------------------------------------------- render
  const busy = isThinking || isSubmitting;

  return (
    <div className="min-h-screen bg-[#FEFEFE] flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-lg font-semibold text-[#1C1C1E]">{survey.title || survey.name}</h1>
            <p className="text-xs text-[#9CA3AF]">
              A conversation, not a form — answer out loud, like you're chatting.
            </p>
          </div>
          {ttsSupported && (
            <button
              onClick={() => {
                setVoiceOn((v) => {
                  if (v) stopSpeaking();
                  else speak(current?.question_text);
                  return !v;
                });
              }}
              className="flex items-center gap-1.5 text-xs font-medium text-[#374151] border border-[#E5E7EB] rounded-lg px-3 py-2 hover:bg-[#FAFBFC] transition-colors"
              title={voiceOn ? 'Mute the moderator voice' : 'Let the moderator speak'}
            >
              {voiceOn ? (
                <Volume2 className="h-4 w-4 text-[#07E5D1]" />
              ) : (
                <VolumeX className="h-4 w-4 text-[#9CA3AF]" />
              )}
              {voiceOn ? 'Voice on' : 'Voice off'}
            </button>
          )}
        </div>

        {timer}

        {/* Progress hint */}
        {(turnCount > 0 || coveredTopics.length > 0) && (
          <div className="mb-4 text-xs text-[#6B7280]">
            {turnCount} {turnCount === 1 ? 'question' : 'questions'} so far
            {coveredTopics.length > 0 && <span> · covered: {coveredTopics.join(', ')}</span>}
          </div>
        )}

        {/* Moderator question */}
        <div className="bg-white border border-[#F1F5F9] rounded-xl p-6 shadow-sm min-h-[120px] flex items-center">
          {isThinking ? (
            <div className="flex items-center gap-3 text-[#6B7280]">
              <Loader2 className="h-5 w-5 animate-spin text-[#07E5D1]" />
              <span className="text-sm">The moderator is thinking…</span>
            </div>
          ) : (
            <div className="w-full">
              {current?.is_mandatory && (
                <span className="inline-block mb-2 text-[10px] font-semibold uppercase tracking-wide text-[#07E5D1] bg-[#E8FCFA] rounded px-2 py-0.5">
                  Key question
                </span>
              )}
              <p className="text-xl text-[#1C1C1E] leading-relaxed">{current?.question_text}</p>
              {ttsSupported && current?.question_text && (
                <button
                  onClick={() => speak(current?.question_text)}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#07E5D1] transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Repeat question
                </button>
              )}
            </div>
          )}
        </div>

        {/* Answer area */}
        {!isThinking && (
          <div className="mt-6">
            <div className="flex flex-col items-center gap-4">
              {/* Mic button */}
              <button
                onClick={micLive ? stopRecording : startRecording}
                disabled={isTranscribing || isSubmitting || expired}
                className={`h-20 w-20 rounded-full flex items-center justify-center transition-all disabled:opacity-50 ${
                  micLive && micStatus === 'listening'
                    ? 'bg-[#EF4444] animate-pulse shadow-lg'
                    : 'bg-[#07E5D1] hover:bg-[#06D4C2] shadow-md'
                }`}
                title={
                  micLive
                    ? UI_TEXT.HUMAN_SURVEY.CONVERSATIONAL.MIC_STOP_TITLE
                    : UI_TEXT.HUMAN_SURVEY.CONVERSATIONAL.MIC_START_TITLE
                }
              >
                {micLive && micStatus === 'listening' ? (
                  <Square className="h-7 w-7 text-white" />
                ) : (
                  <Mic className="h-8 w-8 text-white" />
                )}
              </button>
              <p className="text-xs text-[#9CA3AF] h-4">
                {micLive && micStatus === 'warming'
                  ? UI_TEXT.HUMAN_SURVEY.SESSION.MESSAGES.MIC_WARMING
                  : micLive && micStatus === 'listening'
                    ? UI_TEXT.HUMAN_SURVEY.CONVERSATIONAL.LISTENING_TAP_STOP
                    : isTranscribing
                      ? UI_TEXT.HUMAN_SURVEY.CONVERSATIONAL.TRANSCRIBING
                      : UI_TEXT.HUMAN_SURVEY.CONVERSATIONAL.TAP_MIC_PROMPT}
              </p>
            </div>

            {/* Editable transcript */}
            <textarea
              value={response}
              onChange={(e) => {
                setResponse(e.target.value);
                setLastInputMode(INPUT_MODE.TEXT);
              }}
              disabled={isSubmitting || isTranscribing || expired}
              placeholder={UI_TEXT.HUMAN_SURVEY.CONVERSATIONAL.ANSWER_PLACEHOLDER}
              rows={4}
              className="mt-4 w-full border border-[#E5E7EB] rounded-lg p-3 text-[#374151] text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1] focus:border-transparent resize-none"
            />

            <button
              onClick={handleSubmit}
              disabled={!response.trim() || busy || isTranscribing || expired}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-[#1C1C1E] text-white rounded-lg py-3 text-sm font-semibold hover:bg-[#374151] transition-colors disabled:opacity-40"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isSubmitting
                ? UI_TEXT.HUMAN_SURVEY.CONVERSATIONAL.SENDING
                : UI_TEXT.HUMAN_SURVEY.CONVERSATIONAL.SEND_ANSWER}
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 text-sm text-[#EF4444] bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationalSurveyFlow;
